/**
 * The photo_subject categories, in ONE place (2026-10-22, issue #3608).
 *
 * The Bedrock vision check answers with a CATEGORY, and that category is the whole
 * difference between work a reviewer must do today and work that can wait: a
 * `minor_primary` flag is a child-safety matter, a `not_a_person` flag is usually a
 * plate of food. Until now the queue showed one chip, "Subject", for all of them, so
 * the two `minor_primary` flags on PROD (2026-10-15 and 2026-10-18) could not be found
 * in the tool built to review them — they were located with a SQL query. The review
 * page showed the category; the LIST did not.
 *
 * Pure, with no imports, so the render tests bundle it without the auth provider —
 * same rule as refused-photos-model.ts.
 *
 * The categories mirror `PhotoSubjectCategory` in backend/src/utils/image-moderation.ts.
 * tests/unit/lint/photo-subject-categories-one-place.test.ts fails the build when the
 * two lists drift apart. An unknown category still renders (see categoryText): the
 * backend is the source of truth, and the UI must never hide a refusal it cannot name.
 */

/** Every category the vision check can answer. Mirrors the backend union. */
export const PHOTO_SUBJECT_CATEGORIES = [
  'ok',
  'text_or_screenshot',
  'not_a_person',
  'person_not_main_subject',
  'minor_primary',
  'moderation_unparseable',
] as const;

export type PhotoSubjectCategory = typeof PHOTO_SUBJECT_CATEGORIES[number];

export interface CategoryText {
  /** The full name, on the review page. */
  label: string;
  /** The short name, in the queue's "Flagged For" column. */
  chip: string;
  /** What it means, as a title on the chip and beside the filter. */
  meaning: string;
  /** A child-safety category: it must stand out in the list and be reviewed first. */
  severe?: true;
}

export const PHOTO_SUBJECT_CATEGORY_TEXT: Readonly<Record<PhotoSubjectCategory, CategoryText>> = {
  ok: {
    label: 'OK',
    chip: 'Subject',
    meaning: 'The model judged the subject of the photo acceptable.',
  },
  text_or_screenshot: {
    label: 'Text / screenshot',
    chip: 'Text / screenshot',
    meaning: 'The model read the photo as text, a screenshot or a meme, not as a photo of the member.',
  },
  not_a_person: {
    label: 'Not a person photo',
    chip: 'Not a person',
    meaning: 'The model found no person in the photo.',
  },
  person_not_main_subject: {
    label: 'Person not the main subject',
    chip: 'Not the main subject',
    meaning: 'The model found a person, but judged that person not to be the main subject.',
  },
  minor_primary: {
    label: 'Child as primary subject',
    chip: 'Child as subject',
    // Owner policy 2026-10-27: a child may be in a photo when the adult account owner is
    // clearly in it too. The model can see "an adult"; only a reviewer can see "the owner".
    meaning: 'The model flagged a child in this photo. Review it first: a person must decide it. Restore it only when the account owner is clearly in the photo too.',
    severe: true,
  },
  moderation_unparseable: {
    label: 'Unparseable model output',
    chip: 'Unreadable result',
    meaning: 'The model answer could not be read, so the photo was refused. This is not a judgement about the photo.',
  },
};

/** The category a photo_subject check recorded, or null. `details` arrives as jsonb. */
export function photoSubjectCategoryOf(details: unknown): string | null {
  if (details === null || typeof details !== 'object') return null;
  const value = (details as Record<string, unknown>).category;
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * The texts for a category. A category the backend added and this list does not know
 * keeps its own name and says so, instead of disappearing behind the generic chip.
 */
export function categoryText(category: string | null): CategoryText | null {
  if (category === null) return null;
  const known = (PHOTO_SUBJECT_CATEGORY_TEXT as Record<string, CategoryText>)[category];
  if (known) return known;
  return {
    label: category,
    chip: category,
    meaning: `The model answered "${category}". This admin panel does not know that category yet: open the photo to read the reason.`,
  };
}

/**
 * The queue's "Flagged for" control carries a check type and, for photo_subject, one
 * category: `photo_subject:minor_primary`. One control keeps the child-safety category
 * one click away, instead of behind a second dropdown nobody opens.
 */
export function splitFlaggedFor(value: string): { checkType: string; category: string } {
  const at = value.indexOf(':');
  return at === -1
    ? { checkType: value, category: 'all' }
    : { checkType: value.slice(0, at), category: value.slice(at + 1) };
}

/** Only the explicit-content check records a confidence; see the queue route's filter. */
export const CONFIDENCE_CHECK_TYPE = 'explicit_content';
